import { z } from 'zod';
import { instantSchema, uuidSchema } from '../schema/rows.js';
/**
 * The trash (REC-05, docs/04 §10 rule 5, design D1–D2 of f1-trash-search). A block goes to the
 * trash with its live descendants, all with the same `deleted_at`: the server's
 * `cascade_trash_to_children` would do the same, but offline the subtasks must leave the views at
 * once. Restoring brings back what went with the block (same mark) and its trashed ancestors, so
 * it never stays hidden under a trashed parent (INV-12). The physical deletion is the server's.
 */
/** Days a block stays in the trash before the purge (docs/08 §13). */
export const TRASH_RETENTION_DAYS = 30;
const trashDeleted = (id, deletedAt) => ({
    table: 'blocks',
    op: 'PATCH',
    id: id,
    data: { deleted_at: deletedAt },
});
const trashBlockInput = z.object({
    id: uuidSchema,
    /** Live descendants of the block, at any depth. */
    descendants: z.array(uuidSchema).default([]),
    now: instantSchema.optional(),
});
/** Logical deletion of a block and its live descendants, with its undo. */
export function trashBlock(input) {
    const p = trashBlockInput.parse(input);
    const now = p.now ?? new Date().toISOString();
    const ids = [p.id, ...p.descendants.filter((id) => id !== p.id)];
    return {
        operations: ids.map((id) => trashDeleted(id, now)),
        // The parent first, so no live child is ever left under a trashed parent.
        undo: ids.map((id) => trashDeleted(id, null)),
    };
}
const trashedRow = z.object({ id: uuidSchema, deleted_at: z.string() });
const restoreBlockInput = z.object({
    block: trashedRow,
    /** Trashed descendants of the block; only those with its same mark come back. */
    descendants: z.array(trashedRow).default([]),
    /** Trashed ancestors, from the parent up; they come back without their other children. */
    ancestors: z.array(trashedRow).default([]),
});
/** «Restaurar»: the block, what was trashed with it and its trashed ancestors. */
export function restoreBlock(input) {
    const p = restoreBlockInput.parse(input);
    const together = p.descendants.filter((row) => row.deleted_at === p.block.deleted_at);
    // Parents before children: the root of the chain first.
    const rows = [...[...p.ancestors].reverse(), p.block, ...together];
    return {
        operations: rows.map((row) => trashDeleted(row.id, null)),
        undo: [...rows].reverse().map((row) => trashDeleted(row.id, row.deleted_at)),
    };
}
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * What the Trash lists (design D2): one entry per trashed «root», a trashed block whose parent
 * is not in these rows with its same mark, newest first, with its descendants and the days left.
 */
export function trashEntries(rows, now) {
    const byId = new Map(rows.map((row) => [row.id, row]));
    const children = new Map();
    for (const row of rows) {
        if (row.parent_id === null)
            continue;
        const list = children.get(row.parent_id) ?? [];
        list.push(row);
        children.set(row.parent_id, list);
    }
    const isRoot = (row) => {
        const parent = row.parent_id === null ? undefined : byId.get(row.parent_id);
        return parent?.deleted_at !== row.deleted_at;
    };
    const together = (root) => {
        const out = [];
        const walk = (id) => {
            for (const child of children.get(id) ?? []) {
                if (child.deleted_at !== root.deleted_at)
                    continue;
                out.push(child);
                walk(child.id);
            }
        };
        walk(root.id);
        return out;
    };
    const nowMs = Date.parse(now);
    return rows
        .filter(isRoot)
        .sort((a, b) => (a.deleted_at < b.deleted_at ? 1 : a.deleted_at > b.deleted_at ? -1 : 0))
        .map((block) => {
        const elapsed = Math.floor((nowMs - Date.parse(block.deleted_at)) / DAY_MS);
        return {
            block,
            descendants: together(block),
            daysLeft: Math.max(0, TRASH_RETENTION_DAYS - elapsed),
        };
    });
}
//# sourceMappingURL=trash.js.map